// src/components/Auth/LoginPage.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import IntlTelInput, {
  intlTelInput,
} from "intl-tel-input/react/dist/IntlTelInputWithUtils";
import "intl-tel-input/dist/css/intlTelInput.min.css";
import { saveUserPhone, getRedirectUrl } from "../../utils/storage";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function getIso2FromIsdCode(isdCode) {
  try {
    const countries = intlTelInput.getCountryData();
    const str = String(isdCode);

    const primary = countries.find(
      (c) => String(c.dialCode) === str && c.priority === 0
    );

    if (primary) return primary.iso2;

    const fallback = countries.find((c) => String(c.dialCode) === str);
    return fallback ? fallback.iso2 : "in";
  } catch {
    return "in";
  }
}

export default function LoginPage() {
  const [phone, setPhone] = useState("");
  const [isd, setIsd] = useState("91");
  const [initialCountry, setInitialCountry] = useState("in");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef(null);

  const navigate = useNavigate();
  const toast = useToast();

  // Prefill from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mobileFromUrl = params.get("mobile");
    const isdFromUrl = params.get("isdcode");

    if (isdFromUrl) {
      const iso2 = getIso2FromIsdCode(isdFromUrl);
      setIsd(String(isdFromUrl));
      setInitialCountry(iso2);
    }

    if (mobileFromUrl) {
      const cleaned = mobileFromUrl.replace(/\D/g, "");
      setPhone(cleaned);
    }

    console.log("🔐 LoginPage mounted");
    console.log("📍 Redirect URL:", getRedirectUrl());
  }, []);

  const validatePhone = (num, isdCode) => {
    if (String(isdCode) === "91") {
      return /^[6-9]\d{9}$/.test(num);
    }
    return /^\d{5,15}$/.test(num);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!phone) {
      setError("Please enter registered mobile number");
      return;
    }

    if (!validatePhone(phone, isd)) {
      setError(
        String(isd) === "91"
          ? "Enter valid 10-digit registered Indian number"
          : "Enter valid registered mobile number"
      );
      return;
    }

    setIsLoading(true);

    try {
      const body = new URLSearchParams({
        mobile: phone,
        isd: isd,
        appcode: "RSFM",
      });

      const response = await fetch(`${API_BASE}/customer/me/loginotp`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const data = await response.json();

      if (!response.ok || data.status === "error") {
        throw new Error(data.message || "OTP failed");
      }

      saveUserPhone(phone);

      toast({
        title: "OTP Sent",
        description: `+${isd} ${phone}`,
        status: "success",
        duration: 3000,
        position: "top",
      });

      navigate("/verify-otp");
    } catch (err) {
      setError(err.message);

      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 4000,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const cardBg = useColorModeValue("white", "gray.800");
  const pageBg = useColorModeValue("gray.100", "gray.900");

  return (
    <Flex minH="100vh" align="center" justify="center" bg={pageBg}>
      <Box
        w="100%"
        maxW="400px"
        bg={cardBg}
        px={8}
        py={10}
        borderRadius="lg"
        boxShadow="lg"
      >
        <Box mb={8} textAlign="center">
          <Text fontSize="28px" fontWeight="700">
            Welcome
          </Text>
          <Text fontSize="14px" color="gray.500">
            Enter your mobile registered number
          </Text>
        </Box>

        <form onSubmit={handleSubmit}>
          <FormControl isInvalid={!!error} mb={6}>
            <FormLabel fontSize="14px" fontWeight="600">
            Registered Mobile Number
            </FormLabel>

            {/* FIXED INPUT */}
            <Box
              sx={{
                ".iti": { width: "100%" },
                ".iti input": {
                  width: "100%",
                  height: "48px",
                  borderRadius: "8px",
                  border: "1px solid #CBD5E0",
                  paddingLeft: "90px !important",
                  fontSize: "16px",
                },
              }}
            >
              <IntlTelInput
                inputRef={inputRef}
                initialCountry={initialCountry}
                initialValue={`+${isd}${phone}`}
                separateDialCode={true}
                onChangeNumber={(val) => {
                  const digits = val.replace(/\D/g, "");

                  const countries = intlTelInput.getCountryData();
                  const found = countries.find((c) =>
                    digits.startsWith(c.dialCode)
                  );

                  if (found) {
                    setIsd(found.dialCode);
                    setPhone(digits.slice(found.dialCode.length));
                  } else {
                    setPhone(digits);
                  }

                  setError("");
                }}
                onChangeCountry={(iso2) => {
                  const countries = intlTelInput.getCountryData();
                  const found = countries.find((c) => c.iso2 === iso2);

                  if (found) {
                    setIsd(found.dialCode);
                  }

                  setInitialCountry(iso2);
                  setError("");
                }}
                inputProps={{
                  placeholder:
                    isd === "91"
                      ? "10-digit mobile number"
                      : "Mobile number",
                }}
              />
            </Box>

            {error && (
              <FormErrorMessage fontSize="13px">
                {error}
              </FormErrorMessage>
            )}
          </FormControl>

          <Button
            type="submit"
            w="100%"
            h="48px"
            bg="cyan.500"
            color="white"
            fontWeight="600"
            borderRadius="8px"
            isLoading={isLoading}
            loadingText="Sending..."
            _hover={{ bg: "cyan.600" }}
            isDisabled={!phone || phone.length < 5}
          >
            Send OTP
          </Button>
        </form>

        <Box mt={6} textAlign="center">
          <Text fontSize="12px" color="gray.500">
            OTP will be sent via SMS
          </Text>
        </Box>
      </Box>
    </Flex>
  );
}